import {
  Avatar,
  DetailGroup,
  Flex,
  Text,
  VisuallyHidden,
} from "@daangn/carotene";
import type { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import { formatDistanceToNowStrict } from "date-fns";

import DefaultUserImage from "~/assets/default_user_avatar.png";
import { getDateFnsLocale } from "~/core/getDateFnsLocale";
import { useLocale } from "~/remix-lib";
import { Link } from "~/remix-lib";
import Image from "~/services/Image";
import { Time } from "~/services/Time";
import { makeThumbnailSrcSet } from "~/utils/makeThumbnailSrcSet";

import * as css from "./Comment.css";

export type CommentProps = {
  content?: string | null;
  regionName?: string | null;
  createdAt?: string | null;
  userNickname?: string | null;
  userProfileImage?: string | null;
  articleThumbnail?: string | null;
  href?: string | null;
  karrotLocalCountryCode: KarrotLocalCountryCode;
  isSeller?: boolean | null;
  isChildrenComment?: boolean;
  isBluredComment: boolean;
};

const Comment = ({
  content,
  regionName,
  createdAt,
  userNickname,
  userProfileImage,
  articleThumbnail,
  href,
  karrotLocalCountryCode,
  isSeller,
  isChildrenComment = false,
  isBluredComment = false,
}: CommentProps) => {
  const { t } = useLocale();

  const isShowingDetailGroup =
    typeof isSeller === "boolean" ||
    typeof regionName === "string" ||
    typeof createdAt === "string";

  return (
    <Flex
      flexDirection="row"
      gap={2}
      className={css.commentWrap({ isChildrenComment })}
    >
      <div className={css.avatarWrap}>
        {href ? (
          <Link href={href}>
            <VisuallyHidden>{userNickname} profile page</VisuallyHidden>
            <Avatar
              src={userProfileImage ?? DefaultUserImage}
              alt="user profile image on comment"
              size={36}
            />
          </Link>
        ) : (
          <Avatar
            src={userProfileImage ?? DefaultUserImage}
            alt="user profile image on comment"
            size={36}
          />
        )}
      </div>
      <Flex flexDirection="row" width="full" justifyContent="spaceBetween">
        <Flex flexDirection="column" width="full">
          {href ? (
            <Link href={href}>
              <VisuallyHidden>{userNickname} profile page</VisuallyHidden>
              <Text
                size="medium"
                weight="strong"
                color={
                  !userNickname || isBluredComment ? "neutralSubtle" : "neutral"
                }
              >
                {userNickname ?? t("UserPage.withdrawn_user")}
              </Text>
            </Link>
          ) : (
            <Text
              size="medium"
              weight="strong"
              color={
                !userNickname || isBluredComment ? "neutralSubtle" : "neutral"
              }
            >
              {userNickname ?? t("UserPage.withdrawn_user")}
            </Text>
          )}
          {isShowingDetailGroup ? (
            <DetailGroup size="small">
              {typeof isSeller === "boolean" ? (
                <span>
                  {isSeller ? t("UserPage.seller") : t("UserPage.buyer")}
                </span>
              ) : null}
              {typeof regionName === "string" ? (
                <span>{regionName}</span>
              ) : null}
              {typeof createdAt === "string" ? (
                <Time dateTime={createdAt}>
                  {formatDistanceToNowStrict(new Date(createdAt), {
                    addSuffix: true,
                    locale: getDateFnsLocale({
                      karrotLocalCountryCode,
                    }),
                  })}
                </Time>
              ) : null}
            </DetailGroup>
          ) : null}
          <Text
            size="medium"
            weight="default"
            className={css.commentContent}
            color={
              !userNickname || isBluredComment ? "neutralSubtle" : "neutral"
            }
          >
            {content}
          </Text>
        </Flex>
        <Flex justifyContent="center" alignItems="center">
          {articleThumbnail && (
            <Image
              imgClassName={css.commentArticleThumbnail}
              src={makeThumbnailSrcSet(articleThumbnail, "small")}
              alt="comment article thumbnail"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Comment;
